import { useEffect, useState, useRef } from "react";

/**
 * Breaks a millisecond duration into whole hours / minutes / seconds.
 * Never returns negative values — a fully-elapsed timer reads 00:00:00.
 *
 * @param {number} totalMs
 * @returns {{ hours: number, minutes: number, seconds: number, totalMs: number, expired: boolean }}
 */
export const getTimeParts = (totalMs) => {
  const clamped = Math.max(0, totalMs);
  const totalSeconds = Math.floor(clamped / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return {
    hours,
    minutes,
    seconds,
    totalMs: clamped,
    expired: clamped <= 0,
  };
};

/**
 * Formats a duration as HH:MM:SS. Hours are NOT capped at 24 — a 60-hour
 * window reads "60:00:00".
 *
 * @param {number} totalMs
 * @returns {string}
 */
export const formatHMS = (totalMs) => {
  const { hours, minutes, seconds } = getTimeParts(totalMs);
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
};

/**
 * Live HH:MM:SS countdown to `deadline`, ticking once a second. A null or
 * invalid deadline yields an immediately-expired zeroed result, no interval.
 *
 * @param {Date|string|number|null|undefined} deadline
 * @returns {{ hours: number, minutes: number, seconds: number, totalMs: number, expired: boolean, formatted: string }}
 */
export const useCountdown = (deadline) => {
  const deadlineMs = deadline != null ? new Date(deadline).getTime() : NaN;
  const hasValidDeadline = Number.isFinite(deadlineMs);

  const computeRemaining = () =>
    hasValidDeadline ? deadlineMs - Date.now() : 0;

  const [remainingMs, setRemainingMs] = useState(computeRemaining);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!hasValidDeadline) {
      setRemainingMs(0);
      return undefined;
    }

    // Sync immediately so a deadline change reflects without waiting a tick.
    setRemainingMs(deadlineMs - Date.now());

    intervalRef.current = setInterval(() => {
      const next = deadlineMs - Date.now();
      setRemainingMs(next);
      if (next <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [deadlineMs, hasValidDeadline]);

  const parts = getTimeParts(remainingMs);
  return {
    ...parts,
    formatted: formatHMS(remainingMs),
  };
};

/**
 * Zero-pads a number to two digits (e.g. 5 -> "05").
 *
 * @param {number} value
 * @returns {string}
 */
const pad2 = (value) => String(value).padStart(2, "0");

export default useCountdown;
